import React, { useState, useRef, useEffect } from 'react';
import { Send, Lock } from 'lucide-react';

import { ChatEngine } from '@/core/application/chat/ChatEngine';
import { ChatMessage } from '@/core/infrastructure/local/ChatLocalDB';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';

const addUniqueMessage = (prev: ChatMessage[], newMsg: ChatMessage) => {
  if (prev.some(m => m.id === newMsg.id)) return prev;
  return [...prev, newMsg];
};

const mergeHistorial = (prev: ChatMessage[], historial: ChatMessage[]) => {
  const merged = [...prev];
  for (const h of historial) {
    if (!merged.some(m => m.id === h.id)) {
      merged.push(h);
    }
  }
  return merged.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
};

interface TransactionalChatProps {
  citaId: string;
  profesionalUuid: string;
  pacienteUuid: string;
}

export function TransactionalChat({ citaId, profesionalUuid, pacienteUuid }: Readonly<TransactionalChatProps>) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [engine, setEngine] = useState<ChatEngine | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const maxLength = 250;
  
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user) setCurrentUserId(data.session.user.id);
    });
  }, []);

  // Iniciar Motor
  useEffect(() => {
    if (!profesionalUuid || !pacienteUuid) return;

    const chatEngine = new ChatEngine(
      citaId,
      profesionalUuid,
      pacienteUuid
    );

    let isMounted = true;
    chatEngine.start((newMsg) => {
      if (isMounted) {
        setMessages(prev => addUniqueMessage(prev, newMsg));
      }
    }).then(historial => {
      if (isMounted) {
        setMessages(prev => mergeHistorial(prev, historial));
      }
    });

    setEngine(chatEngine);

    return () => {
      isMounted = false;
      chatEngine.stop();
    };
  }, [citaId, profesionalUuid, pacienteUuid]);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll al último mensaje
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e?: React.SyntheticEvent) => {
    if (e) e.preventDefault();
    if (!inputValue.trim() || !engine || cooldown > 0) return;

    const textoPlano = inputValue.trim();
    setInputValue(''); // Optimistic clear
    
    // Iniciar Cooldown de 3 segundos
    setCooldown(3);
    const interval = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    try {
      await engine.sendMessage(textoPlano);
      // El onMessage callback ya agregará el mensaje localmente
    } catch (error) {
      console.error('Error al enviar:', error);
    }
  };

  const formatTime = (isoString: string) => {
    const d = new Date(isoString);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '450px', backgroundColor: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
      {/* Banner Educativo / Chat Temporal */}
      <div style={{ padding: '12px', backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Lock size={16} color="#64748B" />
        <p style={{ margin: 0, fontSize: '13px', color: '#64748B', lineHeight: '1.4' }}>
          <strong>Chat Temporal:</strong> Los mensajes son de soporte inmediato y no se guardan permanentemente en el servidor.{' '}
          <em>Tip de seguridad: Recuerda proteger el acceso a tus dispositivos.</em>
        </p>
      </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', color: '#94A3B8', marginTop: '20px', fontSize: '14px' }}>
              No hay mensajes. Escribe para resolver dudas sobre el pago.
            </div>
          )}
          
          {messages.map((msg) => {
            const isMe = msg.emisor_id === currentUserId;
          return (
            <div key={msg.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
              <div style={{ 
                maxWidth: '80%', 
                backgroundColor: isMe ? '#0052CC' : '#FFFFFF', 
                color: isMe ? '#FFFFFF' : '#0A2540',
                padding: '12px 16px',
                borderRadius: '16px',
                borderBottomRightRadius: isMe ? '4px' : '16px',
                borderBottomLeftRadius: isMe ? '16px' : '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                border: isMe ? 'none' : '1px solid #E2E8F0'
              }}>
                <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.4' }}>{msg.texto}</p>
                <div style={{ textAlign: 'right', marginTop: '4px', fontSize: '10px', color: isMe ? 'rgba(255,255,255,0.7)' : '#94A3B8' }}>
                  {formatTime(msg.timestamp)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={{ backgroundColor: '#FFFFFF', padding: '12px 16px', borderTop: '1px solid #E2E8F0' }}>
        <form onSubmit={handleSend} style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, position: 'relative', display: 'flex' }}>
            <textarea
              value={inputValue}
              disabled={cooldown > 0}
              onChange={(e) => setInputValue(e.target.value.slice(0, maxLength))}
              placeholder={cooldown > 0 ? "Espera un momento..." : "Escribe un mensaje de soporte..."}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                border: '1px solid #CBD5E1',
                borderRadius: '12px',
                padding: '12px 12px 28px 12px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'none',
                minHeight: '44px',
                maxHeight: '120px',
                outline: 'none',
                lineHeight: '1.4'
              }}
              rows={Math.min(4, inputValue.split('\n').length)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
            />
            <span style={{ position: 'absolute', bottom: '8px', right: '12px', fontSize: '11px', color: inputValue.length >= maxLength ? '#EF4444' : '#94A3B8' }}>
              {inputValue.length} / {maxLength}
            </span>
          </div>
          
          <button 
            type="submit"
            disabled={!inputValue.trim() || cooldown > 0}
            style={{ 
              backgroundColor: inputValue.trim() && cooldown === 0 ? '#0052CC' : '#E2E8F0', 
              color: '#FFF', 
              border: 'none', 
              width: '44px', 
              height: '44px', 
              borderRadius: '22px', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              cursor: inputValue.trim() && cooldown === 0 ? 'pointer' : 'not-allowed',
              transition: 'background-color 0.2s',
              flexShrink: 0
            }}
          >
            {cooldown > 0 ? <span style={{ fontWeight: 800, fontSize: '15px' }}>{cooldown}</span> : <Send size={20} />}
          </button>
        </form>
        <style>{`
          @keyframes spin { 100% { transform: rotate(360deg); } }
        `}</style>
      </div>
    </div>
  );
}
